import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  CheckCircle, XCircle, Clock, Search, Loader2,
  Users, Package, RefreshCw, Filter
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending:   { label: 'Pending',  color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock },
  approved:  { label: 'Approved', color: 'bg-green-100 text-green-700 border-green-200',   icon: CheckCircle },
  rejected:  { label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-200',          icon: XCircle },
};

const CATEGORY_STYLES = {
  'Microsoft 365':    'bg-blue-50 text-blue-700 border-blue-200',
  'Google Workspace': 'bg-red-50 text-red-700 border-red-200',
  'Email Hosting':    'bg-amber-50 text-amber-700 border-amber-200',
};

const CYCLE_LABELS = {
  monthly: 'Monthly', quarterly: 'Quarterly',
  half_yearly: 'Half Yearly', yearly: 'Yearly',
};

export default function SaaSOrders() {
  const [orders, setOrders]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [statusFilter, setStatus] = useState('all');
  const [catFilter, setCat]       = useState('all');
  const [reviewModal, setReview]  = useState(null);  // { order, action }
  const [notes, setNotes]         = useState('');
  const [acting, setActing]       = useState(false);

  const load = useCallback(async (st) => {
    setLoading(true);
    try {
      const params = st && st !== 'all' ? `?order_status=${st}` : '';
      const res = await axios.get(`${API}/admin/saas-order-requests${params}`);
      setOrders(res.data);
    } catch {
      toast.error('Failed to load SaaS orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(statusFilter); }, [load, statusFilter]);

  const handleReview = async (action) => {
    if (!reviewModal) return;
    setActing(true);
    try {
      await axios.put(
        `${API}/admin/order-requests/${reviewModal.order.id}/${action}`,
        { review_notes: notes }
      );
      toast.success(`Order ${action}d`);
      setReview(null);
      setNotes('');
      load(statusFilter);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setActing(false);
    }
  };

  const categories = ['all', ...new Set(orders.map(o => o.product_category).filter(Boolean))];

  const filtered = orders.filter(o => {
    const matchSearch = !search ||
      o.product_name?.toLowerCase().includes(search.toLowerCase()) ||
      o.client_name?.toLowerCase().includes(search.toLowerCase()) ||
      o.partner_name?.toLowerCase().includes(search.toLowerCase());
    const matchCat = catFilter === 'all' || o.product_category === catFilter;
    return matchSearch && matchCat;
  });

  const stats = {
    total:    orders.length,
    pending:  orders.filter(o => o.status === 'pending').length,
    approved: orders.filter(o => o.status === 'approved').length,
    rejected: orders.filter(o => o.status === 'rejected').length,
  };

  const fmt = (dt) => {
    if (!dt) return '—';
    return new Date(dt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">SaaS License Orders</h1>
            <p className="text-sm text-slate-500 mt-0.5">Microsoft 365, Google Workspace, and Email Hosting orders</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => load(statusFilter)}>
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total Orders',  value: stats.total,    color: 'text-slate-700' },
            { label: 'Pending',       value: stats.pending,   color: 'text-yellow-600' },
            { label: 'Approved',      value: stats.approved,  color: 'text-green-600' },
            { label: 'Rejected',      value: stats.rejected,  color: 'text-red-600' },
          ].map(s => (
            <Card key={s.label} className="border border-slate-200">
              <CardContent className="p-4">
                <p className="text-xs text-slate-400 mb-1">{s.label}</p>
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card className="border border-slate-200">
          <CardContent className="p-3 flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400 pointer-events-none" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search product, client, partner..."
                className="pl-9"
                data-testid="saas-search"
              />
            </div>
            {/* Status filter */}
            <div className="flex gap-1.5">
              {['all', 'pending', 'approved', 'rejected'].map(s => (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors capitalize ${
                    statusFilter === s
                      ? 'bg-slate-800 text-white border-slate-800'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                  }`}
                  data-testid={`status-filter-${s}`}
                >
                  {s === 'all' ? 'All' : s}
                </button>
              ))}
            </div>
            {/* Category filter */}
            <div className="flex gap-1.5">
              {categories.map(c => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    catFilter === c
                      ? 'bg-blue-700 text-white border-blue-700'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                  }`}
                  data-testid={`cat-filter-${c}`}
                >
                  {c === 'all' ? 'All Categories' : c}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Orders table */}
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
        ) : filtered.length === 0 ? (
          <Card className="border-dashed"><CardContent className="py-16 text-center text-slate-400">
            <Package className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p>No SaaS orders found</p>
          </CardContent></Card>
        ) : (
          <Card className="border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    {['Product', 'Category', 'Client', 'Partner', 'Users/Accs', 'Cycle', 'Amount', 'Date', 'Status', 'Action'].map(h => (
                      <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {filtered.map(o => {
                    const sm = STATUS_META[o.status] || STATUS_META.pending;
                    const SI = sm.icon;
                    const catStyle = CATEGORY_STYLES[o.product_category] || 'bg-slate-100 text-slate-700';
                    const total = (o.unit_price || 0) * (o.quantity || 1);
                    return (
                      <tr key={o.id} className="hover:bg-slate-50 transition-colors" data-testid={`order-row-${o.id}`}>
                        <td className="px-4 py-3 font-medium text-slate-800 max-w-48">
                          <span className="line-clamp-2">{o.product_name}</span>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={`text-xs whitespace-nowrap ${catStyle}`}>
                            {o.product_category}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-slate-600 max-w-32">
                          <span className="line-clamp-1">{o.client_name}</span>
                        </td>
                        <td className="px-4 py-3 text-slate-500 max-w-32">
                          <span className="line-clamp-1">{o.partner_name}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 text-slate-600 whitespace-nowrap">
                            <Users className="h-3.5 w-3.5 text-slate-400" />
                            <span className="font-semibold">{o.quantity || 1}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                          {CYCLE_LABELS[o.billing_cycle] || o.billing_cycle}
                        </td>
                        <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">
                          ₹{total.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-slate-400 whitespace-nowrap text-xs">
                          {fmt(o.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={`text-xs whitespace-nowrap ${sm.color}`}>
                            <SI className="h-3 w-3 mr-1 inline" />
                            {sm.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          {o.status === 'pending' && (
                            <div className="flex gap-1.5">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2.5 text-green-700 border-green-300 hover:bg-green-50"
                                onClick={() => { setReview({ order: o, action: 'approve' }); setNotes(''); }}
                                data-testid={`approve-${o.id}`}
                              >
                                <CheckCircle className="h-3.5 w-3.5 mr-1" /> Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2.5 text-red-700 border-red-300 hover:bg-red-50"
                                onClick={() => { setReview({ order: o, action: 'reject' }); setNotes(''); }}
                                data-testid={`reject-${o.id}`}
                              >
                                <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                              </Button>
                            </div>
                          )}
                          {o.status !== 'pending' && (
                            <span className="text-xs text-slate-400 italic">
                              {o.review_notes || '—'}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Approve / Reject modal */}
      <Dialog open={!!reviewModal} onOpenChange={(o) => { if (!o) { setReview(null); setNotes(''); } }}>
        <DialogContent className="max-w-sm" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${reviewModal?.action === 'approve' ? 'text-green-700' : 'text-red-700'}`}>
              {reviewModal?.action === 'approve'
                ? <><CheckCircle className="h-5 w-5" /> Approve Order</>
                : <><XCircle className="h-5 w-5" /> Reject Order</>
              }
            </DialogTitle>
          </DialogHeader>
          {reviewModal && (
            <div className="space-y-4 pt-1">
              <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
                <p><span className="text-slate-400">Product:</span> <span className="font-medium">{reviewModal.order.product_name}</span></p>
                <p><span className="text-slate-400">Client:</span> <span className="font-medium">{reviewModal.order.client_name}</span></p>
                <p><span className="text-slate-400">Quantity:</span> <span className="font-medium">{reviewModal.order.quantity || 1} user(s)</span></p>
                <p><span className="text-slate-400">Cycle:</span> <span className="font-medium">{CYCLE_LABELS[reviewModal.order.billing_cycle] || reviewModal.order.billing_cycle}</span></p>
                <p><span className="text-slate-400">Amount:</span> <span className="font-semibold text-slate-800">₹{((reviewModal.order.unit_price || 0) * (reviewModal.order.quantity || 1)).toLocaleString()}</span></p>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">Review Notes <span className="text-slate-400 font-normal">(optional)</span></label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={2}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Add a note for the client..."
                  data-testid="review-notes"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => { setReview(null); setNotes(''); }}>Cancel</Button>
                <Button
                  size="sm"
                  className={reviewModal.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                  onClick={() => handleReview(reviewModal.action)}
                  disabled={acting}
                  data-testid="confirm-review-btn"
                >
                  {acting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                  Confirm {reviewModal.action === 'approve' ? 'Approval' : 'Rejection'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
